import React, { useEffect, useMemo, useState } from "react";
import { Building2, CheckCircle2, Landmark, Plus, Smartphone } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import BottomNav from "../components/BottomNav";
import { queueDashboardNotice } from "../utils/appInteractions";

const UPI_WITHDRAW_LIMIT = 2000;

function Withdraw() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [method, setMethod] = useState("bank");
  const [form, setForm] = useState({
    amount: "",
    bank_name: "",
    account_holder: "",
    account_number: "",
    ifsc: "",
    upi_id: "",
    save_bank: true,
  });
  const [savedBanks, setSavedBanks] = useState([]);
  const [savedUpis, setSavedUpis] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState("");
  const [selectedUpiId, setSelectedUpiId] = useState("");
  const [showBankForm, setShowBankForm] = useState(false);
  const [showUpiForm, setShowUpiForm] = useState(false);
  const [wallet, setWallet] = useState({ winnings: 0, balance: 0 });
  const [settings, setSettings] = useState({ min_withdrawal: "500", max_withdrawal: "100000" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    API.get(`/bank-accounts/${user.id}`)
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        const banks = rows.filter((account) => account.account_number && !String(account.account_number).startsWith("UPI-"));
        const upis = rows.filter((account) => String(account.account_number || "").startsWith("UPI-"));
        setSavedBanks(banks);
        setSavedUpis(upis);
        setShowBankForm(banks.length === 0);
        setShowUpiForm(upis.length === 0);
        if (banks[0]) applyBank(banks[0]);
        if (upis[0]) applyUpi(upis[0]);
      })
      .catch(() => {
        setSavedBanks([]);
        setSavedUpis([]);
        setShowBankForm(true);
        setShowUpiForm(true);
      });

    API.get(`/wallet/${user.id}`)
      .then((res) => setWallet(res.data || { winnings: 0, balance: 0 }))
      .catch(() => setWallet({ winnings: 0, balance: 0 }));

    API.get("/settings/public")
      .then((res) => setSettings((current) => ({ ...current, ...(res.data || {}) })))
      .catch(() => {});
  }, [navigate, user]);

  const amount = Number(form.amount || 0);
  const minWithdrawal = Number(settings.min_withdrawal || 500);
  const maxWithdrawal = Number(settings.max_withdrawal || 100000);
  const upiBlocked = method === "upi" && amount > UPI_WITHDRAW_LIMIT;

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const applyBank = (account) => {
    setSelectedBankId(String(account.id || ""));
    setForm((current) => ({
      ...current,
      bank_name: account.bank_name || "",
      account_holder: account.account_holder || "",
      account_number: account.account_number || "",
      ifsc: account.ifsc || "",
    }));
  };

  const applyUpi = (account) => {
    setSelectedUpiId(String(account.id || ""));
    setForm((current) => ({
      ...current,
      upi_id: String(account.account_number || "").replace(/^UPI-/i, ""),
    }));
  };

  const resetBankForm = () => {
    setSelectedBankId("");
    setShowBankForm(true);
    setForm((current) => ({ ...current, bank_name: "", account_holder: "", account_number: "", ifsc: "" }));
  };

  const resetUpiForm = () => {
    setSelectedUpiId("");
    setShowUpiForm(true);
    setForm((current) => ({ ...current, upi_id: "" }));
  };

  const handleWithdraw = async () => {
    if (!user?.id) return alert("Please login first");
    if (!amount) return alert("Enter amount");
    if (amount < minWithdrawal) return alert(`Minimum withdrawal is Rs ${minWithdrawal}`);
    if (amount > maxWithdrawal) return alert(`Maximum withdrawal is Rs ${maxWithdrawal}`);
    if (method === "upi" && amount > UPI_WITHDRAW_LIMIT) return alert("UPI withdrawal is available only up to Rs 2000. Please select bank withdrawal.");

    const payload = {
      user_id: user.id,
      amount,
      save_bank: form.save_bank,
    };

    if (method === "upi") {
      if (!form.upi_id) return alert("Enter UPI ID");
      Object.assign(payload, {
        bank_name: "UPI",
        account_holder: user?.name || user?.phone || "UPI User",
        account_number: `UPI-${form.upi_id}`,
        ifsc: "UPI",
      });
    } else {
      if (!form.bank_name || !form.account_holder || !form.account_number || !form.ifsc) {
        return alert("Enter complete bank details");
      }
      Object.assign(payload, {
        bank_name: form.bank_name,
        account_holder: form.account_holder,
        account_number: form.account_number,
        ifsc: form.ifsc,
      });
    }

    setBusy(true);
    try {
      await API.post("/request-withdraw", payload);
      queueDashboardNotice(`Withdraw request for Rs ${amount.toFixed(2)} submitted successfully.`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to send withdraw request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={page}>
      <section style={card}>
        <div style={hero}>
          <span style={heroIcon}><Landmark size={26} /></span>
          <div>
            <h2 style={title}>Withdraw</h2>
            <p style={subtitle}>Withdraw winnings by bank or UPI. UPI is available up to Rs 2000.</p>
          </div>
        </div>

        <div style={balanceInfo}>
          <div style={balanceTile}>
            <span style={balanceLabel}>Winning Balance</span>
            <strong style={balanceValueGreen}>{`Rs ${Number(wallet.winnings || 0).toFixed(2)}`}</strong>
          </div>
          <div style={balanceTile}>
            <span style={balanceLabel}>Minimum</span>
            <strong style={balanceValue}>{`Rs ${minWithdrawal.toFixed(0)}`}</strong>
          </div>
        </div>

        <label style={label}>Amount</label>
        <input style={input} type="number" inputMode="numeric" placeholder="Enter amount" value={form.amount} onChange={(event) => updateField("amount", event.target.value)} />

        <div style={methodTabs}>
          <button type="button" style={method === "bank" ? activeMethod : methodButton} onClick={() => setMethod("bank")}>
            <Building2 size={18} /> Bank
          </button>
          <button type="button" style={method === "upi" ? activeMethod : methodButton} onClick={() => setMethod("upi")}>
            <Smartphone size={18} /> UPI
          </button>
        </div>

        {method === "upi" && upiBlocked ? <div style={warningBox}>UPI withdrawal is only allowed up to Rs {UPI_WITHDRAW_LIMIT}. Select Bank for this amount.</div> : null}

        {method === "bank" ? (
          <section style={detailSection}>
            <div style={detailHeader}>
              <strong>Bank Details</strong>
              <button type="button" style={plusButton} onClick={resetBankForm}><Plus size={18} /></button>
            </div>
            {savedBanks.length > 0 ? (
              <div style={savedGrid}>
                {savedBanks.map((account) => (
                  <button key={account.id} type="button" style={String(account.id) === selectedBankId ? savedCardActive : savedCard} onClick={() => { applyBank(account); setShowBankForm(false); }}>
                    <span style={savedTitle}>{account.bank_name}</span>
                    <span style={savedMeta}>{maskAccount(account.account_number)}</span>
                    {String(account.id) === selectedBankId ? <CheckCircle2 size={18} /> : null}
                  </button>
                ))}
              </div>
            ) : null}

            {showBankForm ? (
              <div style={formGrid}>
                <input style={input} placeholder="Bank name" value={form.bank_name} onChange={(event) => updateField("bank_name", event.target.value)} />
                <input style={input} placeholder="Account holder name" value={form.account_holder} onChange={(event) => updateField("account_holder", event.target.value)} />
                <input style={input} placeholder="Account number" value={form.account_number} onChange={(event) => updateField("account_number", event.target.value)} />
                <input style={input} placeholder="IFSC code" value={form.ifsc} onChange={(event) => updateField("ifsc", event.target.value.toUpperCase())} />
              </div>
            ) : null}
          </section>
        ) : (
          <section style={detailSection}>
            <div style={detailHeader}>
              <strong>UPI Details</strong>
              <button type="button" style={plusButton} onClick={resetUpiForm}><Plus size={18} /></button>
            </div>
            {savedUpis.length > 0 ? (
              <div style={savedGrid}>
                {savedUpis.map((account) => (
                  <button key={account.id} type="button" style={String(account.id) === selectedUpiId ? savedCardActive : savedCard} onClick={() => { applyUpi(account); setShowUpiForm(false); }}>
                    <span style={savedTitle}>UPI</span>
                    <span style={savedMeta}>{String(account.account_number || "").replace(/^UPI-/i, "")}</span>
                    {String(account.id) === selectedUpiId ? <CheckCircle2 size={18} /> : null}
                  </button>
                ))}
              </div>
            ) : null}

            {showUpiForm ? (
              <input style={input} placeholder="example@upi" value={form.upi_id} onChange={(event) => updateField("upi_id", event.target.value.trim())} />
            ) : null}
          </section>
        )}

        <label style={checkRow}>
          <input type="checkbox" checked={form.save_bank} onChange={(event) => updateField("save_bank", event.target.checked)} />
          Save this detail for next withdrawal
        </label>

        <button type="button" style={upiBlocked || busy ? disabledButton : button} onClick={handleWithdraw} disabled={upiBlocked || busy}>
          {busy ? "Submitting..." : "Submit Withdraw Request"}
        </button>
      </section>

      <BottomNav />
    </div>
  );
}

function maskAccount(value) {
  const text = String(value || "");
  if (text.length <= 4) return text;
  return `•••• ${text.slice(-4)}`;
}

const page = { minHeight: "100vh", background: "radial-gradient(circle at top,#fff7ed,#f4f6fb 46%,#eef2ff)", padding: "14px 12px 86px", boxSizing: "border-box" };
const card = { maxWidth: "560px", margin: "0 auto", background: "#fff", borderRadius: "24px", padding: "14px", boxShadow: "0 16px 36px rgba(15,23,42,0.10)", border: "1px solid rgba(245,158,11,.22)" };
const hero = { display: "grid", gridTemplateColumns: "48px 1fr", gap: "12px", alignItems: "center", padding: "12px", borderRadius: "20px", background: "linear-gradient(135deg,#111827,#1d4ed8)", color: "#fff", marginBottom: "12px" };
const heroIcon = { width: 44, height: 44, borderRadius: "16px", background: "rgba(255,255,255,.14)", display: "grid", placeItems: "center" };
const title = { margin: 0, fontSize: "22px", fontWeight: 900 };
const subtitle = { margin: "4px 0 0", color: "rgba(255,255,255,.78)", fontSize: "12px", lineHeight: 1.4 };
const balanceInfo = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "10px", marginBottom: "12px" };
const balanceTile = { background: "linear-gradient(135deg,#fff7ed,#fffbeb)", border: "1px solid #fde68a", borderRadius: "16px", padding: "11px 12px", display: "grid", gap: "5px" };
const balanceLabel = { color: "#64748b", fontSize: "11px", fontWeight: 800 };
const balanceValue = { color: "#92400e", fontSize: "17px", fontWeight: 900 };
const balanceValueGreen = { ...balanceValue, color: "#16a34a" };
const label = { display: "block", marginBottom: "6px", color: "#334155", fontWeight: 900, fontSize: "12px" };
const input = { width: "100%", height: "44px", marginBottom: "10px", padding: "0 12px", border: "1px solid #d1d5db", borderRadius: "12px", boxSizing: "border-box", fontSize: "14px", fontWeight: 700, outline: "none" };
const methodTabs = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", margin: "2px 0 12px" };
const methodButton = { height: "44px", border: "1px solid #e2e8f0", borderRadius: "14px", background: "#f8fafc", color: "#475569", fontWeight: 900, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "7px" };
const activeMethod = { ...methodButton, background: "linear-gradient(135deg,#2563eb,#7c3aed)", color: "#fff", borderColor: "#93c5fd", boxShadow: "0 10px 20px rgba(37,99,235,.20)" };
const warningBox = { marginBottom: "10px", borderRadius: "14px", padding: "10px 12px", background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa", fontSize: "12px", fontWeight: 800 };
const detailSection = { border: "1px solid #e5e7eb", borderRadius: "18px", padding: "12px", background: "linear-gradient(180deg,#ffffff,#f8fafc)", marginBottom: "12px" };
const detailHeader = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", color: "#0f172a" };
const plusButton = { width: 34, height: 34, border: 0, borderRadius: "50%", background: "#111827", color: "#fff", display: "grid", placeItems: "center" };
const savedGrid = { display: "grid", gap: "8px", marginBottom: "10px" };
const savedCard = { minHeight: 58, border: "1px solid #e2e8f0", borderRadius: "16px", background: "#fff", padding: "9px 11px", display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", textAlign: "left", gap: "2px", color: "#334155" };
const savedCardActive = { ...savedCard, borderColor: "#2563eb", background: "#eff6ff", color: "#1d4ed8" };
const savedTitle = { fontWeight: 900, fontSize: "13px" };
const savedMeta = { gridColumn: "1 / 2", color: "#64748b", fontSize: "12px", fontWeight: 700 };
const formGrid = { display: "grid", gap: "0" };
const checkRow = { display: "flex", alignItems: "center", gap: "8px", color: "#334155", fontWeight: 800, fontSize: "12px", margin: "4px 0 14px" };
const button = { width: "100%", height: "48px", border: 0, borderRadius: "14px", background: "linear-gradient(135deg,#16a34a,#059669)", color: "#fff", fontWeight: 900, cursor: "pointer", boxShadow: "0 12px 24px rgba(22,163,74,.22)" };
const disabledButton = { ...button, background: "#cbd5e1", boxShadow: "none", cursor: "not-allowed" };

export default Withdraw;
